import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { MagnifyingGlass, UsersThree, Briefcase, X } from "@phosphor-icons/react";

// Global search: customers (name/phone/address) + jobs (id/customer/address). Instant top-5 dropdown.
export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ customers: [], jobs: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes({ customers: [], jobs: [] }); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setRes(data); setOpen(true);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  const total = res.customers.length + res.jobs.length;

  return (
    <div ref={boxRef} className="relative w-full max-w-xl" data-testid="global-search">
      <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q.trim().length >= 2 && setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
        placeholder="Search customers or jobs…"
        className="w-full h-10 pl-10 pr-9 bg-white/10 text-white placeholder:text-slate-400 border border-white/15 focus:border-brand-gold focus:bg-white/15 outline-none text-sm transition-colors"
        data-testid="global-search-input"
      />
      {q && (
        <button onClick={() => { setQ(""); setOpen(false); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white" data-testid="global-search-clear">
          <X size={16} />
        </button>
      )}
      {open && (
        <div className="absolute left-0 right-0 top-12 bg-white text-foreground border border-border shadow-xl z-[70] max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
          {loading && <div className="px-4 py-3 text-sm text-muted-foreground">Searching…</div>}
          {!loading && q.trim().length >= 2 && total === 0 && (
            <div className="px-4 py-3 text-sm text-muted-foreground" data-testid="global-search-empty">No matches for “{q}”</div>
          )}
          {res.customers.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground bg-secondary font-semibold flex items-center gap-1.5"><UsersThree size={12} weight="duotone" /> Customers</div>
              {res.customers.map((c) => (
                <button key={c.id} onClick={() => go(`/customers?focus=${c.id}`)} className="w-full text-left px-4 py-2.5 hover:bg-secondary/60 transition-colors flex items-center justify-between gap-3 border-b border-border/50" data-testid={`search-customer-${c.id}`}>
                  <span className="min-w-0">
                    <span className="font-medium block truncate">{c.name}</span>
                    {c.address && <span className="text-xs text-muted-foreground block truncate">{c.address}</span>}
                  </span>
                  {c.phone && <span className="text-xs font-mono text-muted-foreground whitespace-nowrap">{c.phone}</span>}
                </button>
              ))}
            </div>
          )}
          {res.jobs.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground bg-secondary font-semibold flex items-center gap-1.5"><Briefcase size={12} weight="duotone" /> Jobs</div>
              {res.jobs.map((j) => (
                <button key={j.id} onClick={() => go(`/jobs?focus=${j.id}`)} className="w-full text-left px-4 py-2.5 hover:bg-secondary/60 transition-colors flex items-center justify-between gap-3 border-b border-border/50" data-testid={`search-job-${j.id}`}>
                  <span className="min-w-0">
                    <span className="font-medium block truncate">{j.customer_name || "Job"}{j.quote_number ? ` · ${j.quote_number}` : ""}</span>
                    {j.address && <span className="text-xs text-muted-foreground block truncate">{j.address}</span>}
                  </span>
                  {j.status && <span className="text-[10px] uppercase tracking-wider bg-brand-navy text-white px-1.5 py-0.5 whitespace-nowrap">{j.status}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
